import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandSiteApiError, createBrandSite } from "@/lib/brand-site-api";

const schema = z.object({
  title: z.string().trim().min(1, "请填写站名").max(100, "站名不能超过 100 个字符"),
});

type FormValues = z.infer<typeof schema>;

type CreateBrandSiteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  brandId: string;
  onCreated: (siteId: number) => void;
};

/** 创建官网弹窗：只要站名，对齐创建品牌 */
export function CreateBrandSiteDialog({
  open,
  onOpenChange,
  accountId,
  brandId,
  onCreated,
}: CreateBrandSiteDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: "" },
  });

  useEffect(() => {
    if (open) reset({ title: "" });
  }, [open, reset]);

  async function onSubmit({ title }: FormValues) {
    try {
      const payload = await createBrandSite(accountId, { brandId, title });
      onOpenChange(false);
      onCreated(payload.brand_site.id);
    } catch (caught) {
      setError("root", {
        message: caught instanceof BrandSiteApiError ? caught.message : "创建失败，请稍后重试",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>创建官网</DialogTitle>
          <DialogDescription>只要站名。创建后进入这座官网。</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-2">
            <Label htmlFor="create-brand-site-title">站名</Label>
            <Input
              id="create-brand-site-title"
              autoFocus
              placeholder="例如：招聘站"
              autoComplete="off"
              {...register("title")}
              aria-invalid={Boolean(errors.title)}
            />
            {errors.title ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.title.message}
              </p>
            ) : null}
          </div>
          {errors.root ? (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
            >
              {errors.root.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={isSubmitting || !accountId || !brandId}>
              {isSubmitting ? "创建中…" : "创建"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
